import React, { useState, useEffect, useMemo } from 'react';
import {
  Card, Tabs, Form, Input, Button, Select, Table, Tag, Space,
  Tooltip, Popconfirm, Upload, Switch, Alert, Spin, notification,
  Radio, Drawer, List, Avatar, Typography, Divider
} from 'antd';
import {
  SendOutlined,
  UserOutlined,
  TeamOutlined,
  UploadOutlined,
  DeleteOutlined,
  EyeOutlined,
  MailOutlined,
  InboxOutlined,
  PaperClipOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import userService from '../../services/userService';
import messageService from '../../services/messageService';
import MyTable from '../common/MyTable';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminInbox from './AdminInbox';

const { TabPane } = Tabs;
const { TextArea } = Input;
const { Option } = Select;
const { Paragraph } = Typography;

/**
 * AdminMessaging component for sending messages to users
 * Includes features for sending individual messages, broadcast messages,
 * and managing sent messages
 *
 * UPDATE:
 * - Drawer sada uvijek prikazuje inicijalne korisnike i prije pretrage.
 * - Selektovani korisnici ostaju sačuvani kroz pretrage.
 * - Uveden userCache po ID-u da addSelectedUsers radi i za selekcije iz ranijih search rezultata.
 */
const AdminMessaging = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = location.pathname.endsWith('/inbox')
    ? 'inbox'
    : new URLSearchParams(location.search).get('tab') === 'sent' ? 'sent' : 'new';
  // Form and state for new messages
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [messageType, setMessageType] = useState('individual');
  const [userGroups, setUserGroups] = useState([]);
  const [fileList, setFileList] = useState([]);

  // Drawer users state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // Search state for drawer
  const [userSearchText, setUserSearchText] = useState('');
  const [searchText, setSearchText] = useState('');

  // Cache of users by id to preserve multi-select across searches
  const [userCache, setUserCache] = useState({}); // { [id]: user }

  // State for sent messages tab
  const [sentMessages, setSentMessages] = useState([]);
  const [sentMessagesLoading, setSentMessagesLoading] = useState(false);
  const [sentMessagesPagination, setSentMessagesPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // State for message detail drawer
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [messageDetailVisible, setMessageDetailVisible] = useState(false);

  // State for user selection drawer
  const [userSelectionVisible, setUserSelectionVisible] = useState(false);
  const [selectedUserKeys, setSelectedUserKeys] = useState([]);

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  useEffect(() => {
    fetchUserGroups();
  }, []);

  useEffect(() => {
    fetchSentMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sentMessagesPagination.current]);

  // When user selection drawer opens, load initial users
  useEffect(() => {
    if (userSelectionVisible) {
      // reset search input states for cleaner UX
      setUserSearchText('');
      setSearchText('');
      fetchInitialUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSelectionVisible]);

  // Handle searching inside drawer:
  // - if >= 2 chars -> fetchUsers
  // - if empty -> reload initial users
  useEffect(() => {
    if (!userSelectionVisible) return;

    if (searchText && searchText.length >= 2) {
      fetchUsers(searchText);
    } else if (searchText === '') {
      // Instead of clearing table, show default list again
      fetchInitialUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, userSelectionVisible]);

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const upsertUsersToCache = (list = []) => {
    if (!list.length) return;
    setUserCache(prev => {
      const next = { ...prev };
      list.forEach(u => {
        if (u && u.id != null) next[u.id] = u;
      });
      return next;
    });
  };

  const selectedUsersCountLabel = useMemo(() => {
    return selectedUserKeys.length;
  }, [selectedUserKeys]);

  // ---------------------------------------------------------------------------
  // API calls
  // ---------------------------------------------------------------------------

  const fetchUserGroups = async () => {
    try {
      const response = await userService.getUserGroups();
      setUserGroups(response.groups || []);
    } catch (error) {
      console.error('Error fetching user groups:', error);
      notification.error({
        message: 'Failed to load user groups',
        description: error.message || 'Please try again later'
      });
    }
  };

  // Initial load for drawer (no search)
  const fetchInitialUsers = async () => {
    try {
      setUsersLoading(true);
      const response = await userService.getAllCustomers({
        limit: 10000
      });
      const data = response.data || [];
      setUsers(data);
      upsertUsersToCache(data);
    } catch (error) {
      console.error('Error fetching initial users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  // Fetch users based on search text
  const fetchUsers = async (query) => {
    try {
      setUsersLoading(true);
      const response = await userService.getAllCustomers({
        search: query,
        limit: 20
      });
      const data = response.data || [];
      setUsers(data);
      upsertUsersToCache(data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchSentMessages = async () => {
    try {
      setSentMessagesLoading(true);
      const response = await messageService.getSentMessages({
        page: sentMessagesPagination.current,
        limit: sentMessagesPagination.pageSize
      });

      setSentMessages(response.messages || []);
      setSentMessagesPagination(prev => ({
        ...prev,
        total: response.total || 0
      }));
    } catch (error) {
      console.error('Error fetching sent messages:', error);
      notification.error({
        message: 'Failed to load sent messages',
        description: error.message || 'Please try again later'
      });
    } finally {
      setSentMessagesLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  const handleSendMessage = async (values) => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append('subject', values.subject);
      formData.append('message', values.message);
      formData.append('message_type', messageType);
      formData.append('importance', values.importance || 'normal');
      formData.append('send_notification', values.send_notification ? 1 : 0);

      if (messageType === 'individual') {
        formData.append('recipients', JSON.stringify(selectedUsers.map(user => user.id)));
      } else if (messageType === 'group') {
        formData.append('group_id', values.group_id);
      } else if (messageType === 'all') {
        // broadcast all
      }

      fileList.forEach(file => {
        if (file?.originFileObj) {
          formData.append('attachments[]', file.originFileObj);
        }
      });

      const response = await messageService.sendMessage(formData);

      notification.success({
        message: 'Message Sent',
        description: response.message || 'Your message has been sent successfully.'
      });

      form.resetFields();
      setSelectedUsers([]);
      setFileList([]);
      setSelectedUserKeys([]);

      fetchSentMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      notification.error({
        message: 'Failed to send message',
        description: error.response?.data?.message || error.message || 'Please try again later'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = ({ fileList }) => {
    setFileList(fileList);
  };

  // AntD rowSelection onChange passes (selectedRowKeys, selectedRows)
  const handleUserSelectionChange = (newSelectedKeys, selectedRows) => {
    setSelectedUserKeys(newSelectedKeys);
    // ensure selected rows are cached too
    upsertUsersToCache(selectedRows || []);
  };

  const addSelectedUsers = () => {
    // Pull users from cache by selected keys
    const newSelectedUsers = selectedUserKeys
      .map(id => userCache[id])
      .filter(Boolean);

    const combinedUsers = [...selectedUsers];

    newSelectedUsers.forEach(newUser => {
      if (!combinedUsers.some(existingUser => existingUser.id === newUser.id)) {
        combinedUsers.push(newUser);
      }
    });

    setSelectedUsers(combinedUsers);
    setUserSelectionVisible(false);
    // keep keys reset after adding
    setSelectedUserKeys([]);
  };

  const removeUser = (userId) => {
    setSelectedUsers(selectedUsers.filter(user => user.id !== userId));
    setSelectedUserKeys(prev => prev.filter(id => id !== userId));
  };

  const viewMessageDetails = (message) => {
    setSelectedMessage(message);
    setMessageDetailVisible(true);
  };

  const deleteSentMessage = async (messageId) => {
    try {
      await messageService.deleteMessage(messageId);

      notification.success({
        message: 'Message Deleted',
        description: 'The message has been deleted successfully.'
      });

      fetchSentMessages();
    } catch (error) {
      console.error('Error deleting message:', error);
      notification.error({
        message: 'Failed to delete message',
        description: error.message || 'Please try again later'
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getRecipientsText = (message) => {
    if (message.recipient_type === 'all') {
      return 'All Users';
    } else if (message.recipient_type === 'group') {
      return `Group: ${message.group_name || 'Unknown Group'}`;
    } else {
      return `${message.recipient_count || 0} Recipients`;
    }
  };

  // ---------------------------------------------------------------------------
  // Table columns
  // ---------------------------------------------------------------------------

  const sentMessagesColumns = [
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      render: (text, record) => (
        <div>
          <div className="font-medium">{text || 'No Subject'}</div>
          <div className="text-xs text-gray-500">
            {record.message?.length > 50
              ? record.message.substring(0, 50) + '...'
              : record.message}
          </div>
        </div>
      )
    },
    {
      title: 'Recipients',
      key: 'recipients',
      render: (_, record) => (
        <Tag color={
          record.recipient_type === 'all' ? 'blue' :
          record.recipient_type === 'group' ? 'green' :
          'purple'
        }>
          {getRecipientsText(record)}
        </Tag>
      )
    },
    {
      title: 'Sent',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => formatDate(date)
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <Tag color={
          record.read_count === record.recipient_count ? 'green' :
          record.read_count > 0 ? 'orange' :
          'default'
        }>
          {record.read_count || 0}/{record.recipient_count || 0} Read
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => viewMessageDetails(record)}
              className="text-blue-500"
            />
          </Tooltip>

          <Tooltip title="Delete">
            <Popconfirm
              title="Are you sure you want to delete this message?"
              onConfirm={() => deleteSentMessage(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    }
  ];

  const userSelectionColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div className="flex items-center">
          <Avatar
            icon={<UserOutlined />}
            src={record.avatar}
            size="small"
            className="mr-2"
          />
          <span>{text || record.email}</span>
        </div>
      )
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email'
    },
    {
      title: 'Role',
      key: 'role',
      render: (_, record) => (
        <Tag color={record.role === 'admin' ? 'red' : 'blue'}>
          {record.role === 'admin' ? 'Admin' : 'Customer'}
        </Tag>
      )
    }
  ];

  // ---------------------------------------------------------------------------
  // Renderers
  // ---------------------------------------------------------------------------

  const renderNewMessageForm = () => {
    return (
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSendMessage}
        initialValues={{
          subject: '',
          message: '',
          importance: 'normal',
          send_notification: true
        }}
      >
        <div className="mb-4">
          <Radio.Group
            value={messageType}
            onChange={(e) => setMessageType(e.target.value)}
            buttonStyle="solid"
          >
            <Radio.Button value="individual">
              <UserOutlined /> Individual Users
            </Radio.Button>
            <Radio.Button value="group">
              <TeamOutlined /> User Groups
            </Radio.Button>
            <Radio.Button value="all">
              <MailOutlined /> All Users
            </Radio.Button>
          </Radio.Group>
        </div>

        {messageType === 'individual' && (
          <Form.Item
            label="Recipients"
            required
            validateStatus={selectedUsers.length === 0 ? 'error' : 'success'}
            help={selectedUsers.length === 0 ? 'Please select at least one recipient' : null}
          >
            <div className="mb-2">
              <Button
                onClick={() => setUserSelectionVisible(true)}
                icon={<UserOutlined />}
              >
                Select Users
              </Button>
            </div>

            {selectedUsers.length > 0 && (
              <div className="bg-gray-50 p-3 rounded border">
                <div className="mb-2 text-gray-700 font-medium">
                  Selected Recipients ({selectedUsers.length})
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedUsers.map(user => (
                    <Tag
                      key={user.id}
                      closable
                      onClose={() => removeUser(user.id)}
                    >
                      {user.name || user.email}
                    </Tag>
                  ))}
                </div>
              </div>
            )}
          </Form.Item>
        )}

        {messageType === 'group' && (
          <Form.Item
            name="group_id"
            label="User Group"
            rules={[{ required: true, message: 'Please select a user group' }]}
          >
            <Select placeholder="Select a user group">
              {userGroups.map(group => (
                <Option key={group.id} value={group.id}>
                  {group.name} ({group.user_count} users)
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {messageType === 'all' && (
          <Alert
            message="This message will be sent to all users"
            description="Please use this option sparingly as it will send a message to every user in the system."
            type="warning"
            showIcon
            className="mb-4"
          />
        )}

        <Form.Item
          name="subject"
          label="Subject"
          rules={[{ required: true, message: 'Please enter a subject' }]}
        >
          <Input placeholder="Enter message subject" />
        </Form.Item>

        <Form.Item
          name="message"
          label="Message"
          rules={[{ required: true, message: 'Please enter a message' }]}
        >
          <TextArea
            rows={8}
            placeholder="Enter your message here..."
          />
        </Form.Item>

        <div className="flex flex-wrap gap-8 mb-4">
          <Form.Item
            name="importance"
            label="Importance"
            className="mb-0"
          >
            <Select style={{ width: 150 }}>
              <Option value="normal">Normal</Option>
              <Option value="high">High</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="send_notification"
            label="Send Notification"
            valuePropName="checked"
            className="mb-0"
          >
            <Switch />
          </Form.Item>
        </div>

        <Form.Item label="Attachments">
          <Upload
            fileList={fileList}
            onChange={handleFileChange}
            beforeUpload={() => false}
            multiple
          >
            <Button icon={<UploadOutlined />}>Add Attachments</Button>
          </Upload>
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            icon={<SendOutlined />}
            loading={loading}
            disabled={(messageType === 'individual' && selectedUsers.length === 0)}
            className="bg-golden-navy"
          >
            Send Message
          </Button>
        </Form.Item>
      </Form>
    );
  };

  const renderSentMessagesTable = () => {
    return (
      <MyTable
        columns={sentMessagesColumns}
        dataSource={sentMessages}
        rowKey="id"
        loading={sentMessagesLoading}
        pagination={{
          current: sentMessagesPagination.current,
          pageSize: sentMessagesPagination.pageSize,
          total: sentMessagesPagination.total,
          onChange: (page) => setSentMessagesPagination(prev => ({ ...prev, current: page })),
          showSizeChanger: false
        }}
      />
    );
  };

  // ---------------------------------------------------------------------------
  // UI
  // ---------------------------------------------------------------------------

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Messaging</h1>
      </div>

      <Card className="shadow-md mb-6">
        <Tabs
          activeKey={activeTab}
          onChange={(tab) => navigate(tab === 'inbox' ? '/admin/messages/inbox' : `/admin/messages${tab === 'sent' ? '?tab=sent' : ''}`)}
        >
          <TabPane
            tab={
              <span>
                <SendOutlined /> New Message
              </span>
            }
            key="new"
          >
            {renderNewMessageForm()}
          </TabPane>

          <TabPane
            tab={
              <span>
                <InboxOutlined /> Sent Messages
              </span>
            }
            key="sent"
          >
            {renderSentMessagesTable()}
          </TabPane>

          <TabPane tab={<span><InboxOutlined /> Inbox</span>} key="inbox">
            <AdminInbox />
          </TabPane>
        </Tabs>
      </Card>

      {/* User Selection Drawer */}
      <Drawer
        title={`Select Recipients${selectedUsersCountLabel ? ` (${selectedUsersCountLabel} selected)` : ''}`}
        placement="right"
        width={600}
        onClose={() => setUserSelectionVisible(false)}
        open={userSelectionVisible}
        extra={
          <Button
            type="primary"
            onClick={addSelectedUsers}
            disabled={selectedUserKeys.length === 0}
            className="bg-golden-navy"
          >
            Add Selected ({selectedUserKeys.length})
          </Button>
        }
      >
        <div className="mb-4">
          <Input.Search
            placeholder="Search users (min 2 chars)..."
            value={userSearchText}
            onChange={(e) => {
              const v = e.target.value;
              setUserSearchText(v);

              // If user clears the input, reset searchText -> triggers initial list reload
              if (v === '') {
                setSearchText('');
              }
            }}
            onSearch={(value) => setSearchText(value?.trim() || '')}
            loading={usersLoading}
            enterButton
            allowClear
          />
          <div className="text-xs text-gray-500 mt-1">
            Showing default users until you search.
          </div>
        </div>

        {usersLoading ? (
          <div className="flex justify-center py-12">
            <Spin />
          </div>
        ) : (
          <Table
            rowSelection={{
              selectedRowKeys: selectedUserKeys,
              onChange: handleUserSelectionChange,
              preserveSelectedRowKeys: true
            }}
            columns={userSelectionColumns}
            dataSource={users}
            rowKey="id"
            pagination={false}
            size="middle"
            locale={{
              emptyText: searchText
                ? 'No users found'
                : 'No default users available'
            }}
          />
        )}
      </Drawer>

      {/* Message Detail Drawer */}
      <Drawer
        title={selectedMessage?.subject || 'Message Details'}
        placement="right"
        width={600}
        onClose={() => setMessageDetailVisible(false)}
        open={messageDetailVisible}
      >
        {selectedMessage && (
          <div>
            <div className="mb-4">
              <div className="text-gray-500 mb-2">
                Sent on: {formatDate(selectedMessage.created_at)}
              </div>

              <div className="font-medium">
                Recipients: {getRecipientsText(selectedMessage)}
              </div>

              {selectedMessage.recipient_type === 'individual' && selectedMessage.recipients && (
                <div className="mt-2">
                  <Divider orientation="left">Recipient List</Divider>
                  <div className="max-h-40 overflow-y-auto">
                    <List
                      size="small"
                      dataSource={selectedMessage.recipients}
                      renderItem={recipient => (
                        <List.Item>
                          <List.Item.Meta
                            avatar={<Avatar icon={<UserOutlined />} src={recipient.avatar} />}
                            title={recipient.name || recipient.email}
                            description={
                              <Tag color={recipient.read_at ? 'green' : 'default'}>
                                {recipient.read_at ? `Read: ${formatDate(recipient.read_at)}` : 'Not read'}
                              </Tag>
                            }
                          />
                        </List.Item>
                      )}
                    />
                  </div>
                </div>
              )}
            </div>

            <Card className="mb-4">
              <Paragraph className="whitespace-pre-line">
                {selectedMessage.message}
              </Paragraph>
            </Card>

            {selectedMessage.attachments && selectedMessage.attachments.length > 0 && (
              <div>
                <Divider orientation="left">Attachments</Divider>
                <div className="flex flex-wrap gap-2">
                  {selectedMessage.attachments.map((attachment, index) => (
                    <Card
                      key={index}
                      size="small"
                      className="w-full md:w-64 bg-gray-50"
                    >
                      <div className="flex items-center">
                        <div className="mr-2">
                          {attachment.type?.includes('image') ? (
                            <img
                              src={attachment.url}
                              alt={attachment.name}
                              className="w-10 h-10 object-cover rounded"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center">
                              <PaperClipOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
                            </div>
                          )}
                        </div>
                        <div className="flex-grow truncate">
                          <div className="truncate font-medium text-sm">
                            {attachment.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {attachment.size && (
                              <span>{(attachment.size / 1024).toFixed(2)} KB</span>
                            )}
                          </div>
                        </div>
                        <Button
                          type="link"
                          icon={<DownloadOutlined />}
                          size="small"
                          href={attachment.url}
                          target="_blank"
                          download={attachment.name}
                        />
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-4">
              <Alert
                message={`Message Read Rate: ${selectedMessage.read_count || 0}/${selectedMessage.recipient_count || 0}`}
                type="info"
                showIcon
              />
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default AdminMessaging;
